'use client';
// رسوم بيانية SVG خفيفة بلا مكتبات خارجية — شموع، خطوط، مقاييس، منحنيات
import { useId } from 'react';

export interface Candle { t: number; o: number; h: number; l: number; c: number; v: number; }

const UP = '#34d399';
const DOWN = '#fb7185';
const GRID = 'rgba(148,163,184,0.12)';
const TXT = '#8b98ad';

// ---------- رسم الشموع اليابانية مع حجم التداول ----------
export function CandleChart({ candles, height = 320 }: { candles: Candle[]; height?: number }) {
  const W = 800, H = height;
  const volH = Math.round(H * 0.2);
  const priceH = H - volH - 24;
  if (!candles.length) {
    return <div className="flex items-center justify-center h-40 text-xs text-muted-foreground">Waiting for the first candle — the market opens within a minute…</div>;
  }
  const data = candles.slice(-110);
  const highs = Math.max(...data.map(c => c.h));
  const lows = Math.min(...data.map(c => c.l));
  const pad = (highs - lows) * 0.08 || highs * 0.02;
  const maxP = highs + pad, minP = Math.max(0, lows - pad);
  const maxV = Math.max(...data.map(c => c.v), 1);
  const n = data.length;
  const slot = W / n;
  const bodyW = Math.max(2.5, slot * 0.58);
  const y = (p: number) => 8 + (1 - (p - minP) / (maxP - minP)) * (priceH - 8);
  const last = data[n - 1];
  const fmt = (p: number) => p.toFixed(4);

  const gridPrices = [0, 0.25, 0.5, 0.75, 1].map(f => minP + f * (maxP - minP));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ direction: 'ltr' }} role="img" aria-label="AMAN price candlestick chart">
      <defs>
        <linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(148,163,184,0.35)" />
          <stop offset="100%" stopColor="rgba(148,163,184,0.06)" />
        </linearGradient>
      </defs>
      {/* الشبكة وتسميات الأسعار */}
      {gridPrices.map((p, i) => (
        <g key={i}>
          <line x1={0} x2={W} y1={y(p)} y2={y(p)} stroke={GRID} strokeWidth={1} />
          <text x={W - 4} y={y(p) - 3} fontSize={10} fill={TXT} textAnchor="end" fontFamily="JetBrains Mono, monospace">{fmt(p)}</text>
        </g>
      ))}
      {/* حجم التداول */}
      {data.map((c, i) => {
        const h = (c.v / maxV) * (volH - 6);
        return <rect key={`v${i}`} x={i * slot + (slot - bodyW) / 2} y={H - h} width={bodyW} height={h} fill={c.c >= c.o ? 'rgba(52,211,153,0.28)' : 'rgba(251,113,133,0.28)'} />;
      })}
      {/* الشموع */}
      {data.map((c, i) => {
        const up = c.c >= c.o;
        const col = up ? UP : DOWN;
        const yO = y(c.o), yC = y(c.c);
        const top = Math.min(yO, yC), hgt = Math.max(1.2, Math.abs(yC - yO));
        return (
          <g key={i}>
            <line x1={i * slot + slot / 2} x2={i * slot + slot / 2} y1={y(c.h)} y2={y(c.l)} stroke={col} strokeWidth={1.4} />
            <rect x={i * slot + (slot - bodyW) / 2} y={top} width={bodyW} height={hgt} fill={col} rx={0.8} />
          </g>
        );
      })}
      {/* خط السعر الأخير */}
      <line x1={0} x2={W} y1={y(last.c)} y2={y(last.c)} stroke={last.c >= last.o ? UP : DOWN} strokeWidth={1} strokeDasharray="5 4" opacity={0.7} />
      <rect x={2} y={y(last.c) - 10} width={64} height={18} rx={4} fill={last.c >= last.o ? 'rgba(52,211,153,0.15)' : 'rgba(251,113,133,0.15)'} stroke={last.c >= last.o ? UP : DOWN} strokeWidth={0.8} />
      <text x={34} y={y(last.c) + 3} fontSize={11} fontWeight={700} fill={last.c >= last.o ? UP : DOWN} textAnchor="middle" fontFamily="JetBrains Mono, monospace">{fmt(last.c)}</text>
    </svg>
  );
}

// ---------- خط سعر مصغر مع تعبئة ----------
export function Sparkline({ data, height = 56, positive = true }: { data: number[]; height?: number; positive?: boolean }) {
  const gid = useId().replace(/[:]/g, '');
  const W = 240, H = height;
  if (data.length < 2) return <div style={{ height }} />;
  const max = Math.max(...data), min = Math.min(...data);
  const rng = max - min || 1;
  const pts = data.map((v, i) => [ (i / (data.length - 1)) * W, 4 + (1 - (v - min) / rng) * (H - 8) ] as const);
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area = `${line} L${W},${H} L0,${H} Z`;
  const col = positive ? UP : DOWN;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ direction: 'ltr' }} preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id={`sp${gid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={col} stopOpacity={0.35} />
          <stop offset="100%" stopColor={col} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#sp${gid})`} />
      <path d={line} fill="none" stroke={col} strokeWidth={2} />
    </svg>
  );
}

// ---------- منحنى مساحي عام (انبعاث/توقعات) ----------
export function AreaChart({ series, height = 200, color = '#34d399', capLabel, capRatio }: {
  series: number[]; height?: number; color?: string; capLabel?: string; capRatio?: number;
}) {
  const gid = useId().replace(/[:]/g, '');
  const W = 700, H = height;
  if (series.length < 2) return null;
  const max = Math.max(...series, capRatio ?? 0) * 1.05;
  const y = (v: number) => 10 + (1 - v / max) * (H - 30);
  const pts = series.map((v, i) => [ (i / (series.length - 1)) * W, y(v) ] as const);
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area = `${line} L${W},${H - 20} L0,${H - 20} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ direction: 'ltr' }} role="img" aria-label="curve">
      <defs>
        <linearGradient id={`ac${gid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.4} />
          <stop offset="100%" stopColor={color} stopOpacity={0.03} />
        </linearGradient>
      </defs>
      <line x1={0} x2={W} y1={H - 20} y2={H - 20} stroke={GRID} />
      {capRatio !== undefined && (
        <>
          <line x1={0} x2={W} y1={y(capRatio)} y2={y(capRatio)} stroke="#f0b429" strokeDasharray="6 5" strokeWidth={1.2} opacity={0.8} />
          {capLabel && <text x={6} y={y(capRatio) - 6} fontSize={11} fill="#f0b429" fontWeight={700}>{capLabel}</text>}
        </>
      )}
      <path d={area} fill={`url(#ac${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth={2.4} />
    </svg>
  );
}

// ---------- مقياس دائري (نسبة مئوية) ----------
export function DonutGauge({ pct, label, sub, color = '#34d399', size = 148 }: {
  pct: number; label: string; sub?: string; color?: string; size?: number;
}) {
  const clamped = Math.max(0, Math.min(100, pct));
  const r = 56, C = 2 * Math.PI * r;
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} viewBox="0 0 140 140" style={{ direction: 'ltr' }} role="img" aria-label={label}>
        <circle cx="70" cy="70" r={r} fill="none" stroke="rgba(148,163,184,0.14)" strokeWidth={11} />
        <circle
          cx="70" cy="70" r={r} fill="none" stroke={color} strokeWidth={11} strokeLinecap="round"
          strokeDasharray={`${(clamped / 100) * C} ${C}`} transform="rotate(-90 70 70)"
        />
        <text x="70" y="66" textAnchor="middle" fontSize={20} fontWeight={800} fill="#e7edf6" fontFamily="JetBrains Mono, monospace">{clamped.toFixed(2)}%</text>
        <text x="70" y="86" textAnchor="middle" fontSize={10} fill={TXT}>{label}</text>
      </svg>
      {sub && <div className="text-[0.68rem] text-muted-foreground text-center">{sub}</div>}
    </div>
  );
}
